import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import TabFormWrapper from "../../Shared/TabFormWrapper";
import FormFooter from "../../Shared/FormFooter";
import Input from "../../Shared/Input";
import Toggle from "../../Shared/Toggle";
import AlertRed from "../../Shared/AlertRed";
import AlertGreen from "../../Authentication/AlertGreen";
import BookingPageFormSkeleton from "../../Shared/LoadingSkeletons/BookingPageFormSkeleton";
import {
  useBookingPage,
  useUpdateGeneralTab,
} from "../../../hooks/useBookingPage";
import {
  SOCIAL_URL_PATTERNS,
  getSocialMediaValidationMessage,
  autoFixSocialUrl,
} from "../../../utility/socialMediaValidation";

const SOCIAL_FIELDS = [
  {
    key: "instagram",
    label: "Instagram",
    placeholder: "https://instagram.com/yourusername",
  },
  {
    key: "facebook",
    label: "Facebook",
    placeholder: "https://facebook.com/yourpage",
  },
  {
    key: "twitter",
    label: "Twitter/X",
    placeholder: "https://x.com/yourusername",
  },
  {
    key: "linkedin",
    label: "LinkedIn",
    placeholder: "https://linkedin.com/in/yourprofile",
  },
  {
    key: "website",
    label: "Website",
    placeholder: "https://yourwebsite.com",
  },
];

const SocialMediaForm = () => {
  const [apiErrors, setApiErrors] = useState({});
  const [success, setSuccess] = useState(false);

  const { data: configData, isLoading } = useBookingPage();
  const { mutate: updateGeneral, isPending: isUpdating } =
    useUpdateGeneralTab();

  const {
    register,
    handleSubmit,
    setValue,
    reset,
    watch,
    formState: { isDirty, errors: fieldErrors },
  } = useForm({
    defaultValues: {
      showSocialMedia: true,
      socialMediaLinks: {
        instagram: "",
        facebook: "",
        twitter: "",
        linkedin: "",
        website: "",
      },
    },
  });

  const showSocialMedia = watch("showSocialMedia");

  const handleToggleChange = (checked) => {
    setValue("showSocialMedia", checked, { shouldDirty: true });
    setApiErrors({});
    setSuccess(false);
  };

  useEffect(() => {
    if (configData?.data) {
      const formData = {
        showSocialMedia: configData.data.showSocialMedia || false,
        socialMediaLinks: {
          instagram: configData.data.socialMediaLinks?.instagram || "",
          facebook: configData.data.socialMediaLinks?.facebook || "",
          twitter: configData.data.socialMediaLinks?.twitter || "",
          linkedin: configData.data.socialMediaLinks?.linkedin || "",
          website: configData.data.socialMediaLinks?.website || "",
        },
      };
      reset(formData, { keepDirty: false });
    }
  }, [configData, reset]);

  const onSubmit = async (data) => {
    setApiErrors({});
    setSuccess(false);

    updateGeneral(data, {
      onSuccess: () => {
        setSuccess(true);
        setTimeout(() => setSuccess(false), 3000);
      },
      onError: (err) => {
        const errorMessage = err.response?.data?.error?.message;
        const errorDetails = err.response?.data?.error?.details;
        let displayMessage =
          errorMessage || "Failed to update social media settings";
        if (errorDetails && Array.isArray(errorDetails)) {
          displayMessage = errorDetails.join(", ");
        }
        setApiErrors({ title: "Error", message: displayMessage });
      },
    });
  };

  // Auto-fix handler: on blur, attempt to correct the URL
  const handleBlurAutoFix = (platform, fieldName) => (e) => {
    const fixed = autoFixSocialUrl(platform, e.target.value);
    if (fixed !== e.target.value) {
      setValue(fieldName, fixed, { shouldValidate: true, shouldDirty: true });
    } else {
      setValue(fieldName, e.target.value.trim(), { shouldValidate: true });
    }
  };

  if (isLoading) {
    return (
      <TabFormWrapper>
        <BookingPageFormSkeleton showToggle={true} inputCount={5} />
      </TabFormWrapper>
    );
  }

  return (
    <TabFormWrapper onSubmit={handleSubmit(onSubmit)}>
      <div className="px-4 py-6 sm:p-8">
        {/* Social Media Toggle */}
        <div className="mb-6 pb-6 border-b border-ink-100">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Toggle checked={showSocialMedia} onChange={handleToggleChange} />
              <span className="text-sm text-ink-600">
                {showSocialMedia
                  ? "Social media links are visible to clients"
                  : "Social media links are hidden from clients"}
              </span>
            </div>
          </div>
        </div>

        {/* Social media fields — only show when toggle is on */}
        {showSocialMedia && (
          <div className="space-y-6">
            {SOCIAL_FIELDS.map(({ key, label, placeholder }) => {
              const fieldName = `socialMediaLinks.${key}`;
              const fieldError =
                fieldErrors?.socialMediaLinks?.[key]?.message;

              return (
                <Input
                  key={key}
                  label={label}
                  name={fieldName}
                  placeholder={placeholder}
                  errormessage={fieldError}
                  {...register(fieldName, {
                    validate: (value) => {
                      if (!value || value.trim() === "") return true;
                      const pattern = SOCIAL_URL_PATTERNS[key];
                      if (pattern && !pattern.test(value.trim())) {
                        return getSocialMediaValidationMessage(key);
                      }
                      return true;
                    },
                    onBlur: handleBlurAutoFix(key, fieldName),
                  })}
                />
              );
            })}
          </div>
        )}
      </div>

      <FormFooter
        primaryCaption="Save"
        isLoading={isUpdating}
        disabled={!isDirty}
        requiresVerification={false}
      />

      {apiErrors.title && (
        <div className="px-4 sm:px-6 lg:px-8 py-6">
          <AlertRed title={apiErrors.title} message={apiErrors.message} />
        </div>
      )}

      {success && (
        <div className="px-4 sm:px-6 lg:px-8 py-6">
          <AlertGreen
            title="Success"
            message="Social media settings updated successfully"
          />
        </div>
      )}
    </TabFormWrapper>
  );
};

export default SocialMediaForm;
